import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@bynder/design-system';
import {
    NotificationPopupBody,
    NotificationPopupCloseButton,
    NotificationPopupContainer,
    NotificationPopupContent,
    NotificationPopupFooter,
    NotificationPopupHeader,
    NotificationPopupText,
    NotificationPopupTitle,
} from './NotificationPopup.styled';

interface Props {
    onClose: () => void;
}

const NotificationPopup = ({ onClose }: Props) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const countDownRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const disableUpdateRef = useRef(false);
    const [counter, setCounter] = useState(30);

    const handleShow = useCallback(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        !disableUpdateRef.current
            ? modalRef.current?.classList.add('active')
            : modalRef.current?.classList.remove('active');
    }, [disableUpdateRef.current, modalRef.current]);

    const handleCountDown = useCallback(() => {
        setCounter((counter: number) => counter - 1);
    }, []);

    const handleLaterReloadBtnClick = () => {
        clearTimeout(countDownRef.current!);
        disableUpdateRef.current = true;
        modalRef.current?.classList.remove('active');
        onClose();
    };

    const handleReloadBtnClick = () => {
        disableUpdateRef.current = true;
        modalRef.current?.classList.remove('active');
        clearTimeout(countDownRef.current!);
        (location as any)?.reload(true);
        onClose();
    };

    useEffect(() => {
        if (!disableUpdateRef.current) {
            showTimeoutRef.current = setTimeout(handleShow, 1000);
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        disableUpdateRef.current && clearTimeout(showTimeoutRef.current!);

        return () => {
            if (showTimeoutRef.current) {
                clearTimeout(showTimeoutRef.current);
            }
        };
    });

    useEffect(() => {
        if (counter > 0) {
            countDownRef.current = setTimeout(handleCountDown, 1000);
        }
        if (counter === 0 && !disableUpdateRef.current) {
            clearTimeout(countDownRef.current!);
            (location as any)?.reload(true);
        }

        return () => {
            clearTimeout(countDownRef.current!);
        };
    }, [counter, disableUpdateRef]);

    return (
        <NotificationPopupContainer ref={modalRef}>
            <NotificationPopupContent>
                <NotificationPopupHeader>
                    <NotificationPopupTitle>A new version is available</NotificationPopupTitle>
                    <NotificationPopupCloseButton type="button" onClick={handleLaterReloadBtnClick}>
                        <span aria-hidden="true">&times;</span>
                    </NotificationPopupCloseButton>
                </NotificationPopupHeader>
                <NotificationPopupBody>
                    <NotificationPopupText>{`The page will be reloaded in ${counter} seconds, please save any changes`}</NotificationPopupText>
                </NotificationPopupBody>
                <NotificationPopupFooter>
                    <Button variant="secondary" onClick={handleReloadBtnClick}>
                        Refresh now
                    </Button>
                    <Button variant="secondary" onClick={handleLaterReloadBtnClick}>
                        Reload later
                    </Button>
                </NotificationPopupFooter>
            </NotificationPopupContent>
        </NotificationPopupContainer>
    );
};

export default NotificationPopup;
